/**
 * Scans the alpha channel of a rasterized canvas and returns the bounding
 * box of opaque pixels in UV space [0, 1] with origin at the top-left, so it
 * can be fed straight into the shader uniforms.
 *
 * Pixels count as opaque when their alpha is above ALPHA_THRESHOLD — the
 * threshold drops the soft anti-aliased fringe of a cutout.
 */

const ALPHA_THRESHOLD = 16;

export type AlphaContentAnalysis = {
  /** Bounding box of opaque pixels in UV [0,1]; null when none are found */
  bbox: { minX: number; minY: number; maxX: number; maxY: number } | null;
};

export function analyzeAlphaContent(source: HTMLCanvasElement): AlphaContentAnalysis {
  const { width, height } = source;
  const ctx = source.getContext('2d');
  if (!ctx) return { bbox: null };

  const { data } = ctx.getImageData(0, 0, width, height);

  let found = false;
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;

  // Every 2px is plenty for a bbox and cuts the work 4×.
  const stride = 2;

  for (let y = 0; y < height; y += stride) {
    const rowStart = y * width * 4;
    for (let x = 0; x < width; x += stride) {
      if (data[rowStart + x * 4 + 3] < ALPHA_THRESHOLD) continue;
      found = true;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }

  if (!found) return { bbox: null };

  return {
    bbox: {
      minX: minX / width,
      minY: minY / height,
      maxX: maxX / width,
      maxY: maxY / height
    }
  };
}
